import React, { useState } from 'react';
import api from '../api';
import { Upload, FileText, CheckCircle, XCircle, Package, ShoppingCart, Truck, Users, Trash2, AlertTriangle, BarChart3, ArrowRightLeft } from 'lucide-react';

const IMPORT_TYPES = [
  { value: 'auto', label: 'Auto-Detect', icon: null, description: 'Automatically detect CSV type from columns' },
  { value: 'product_details', label: 'Product Details', icon: Package, description: 'Products, SKUs, pricing, inventory' },
  { value: 'stock_levels', label: 'Stock Levels', icon: BarChart3, description: 'Update inventory quantities from inFlow stock export' },
  { value: 'reorder_settings', label: 'Reorder Settings', icon: AlertTriangle, description: 'Update reorder points and reorder quantities' },
  { value: 'sales_orders', label: 'Sales Orders', icon: ShoppingCart, description: 'Customer orders with line items' },
  { value: 'purchase_orders', label: 'Purchase Orders', icon: Truck, description: 'Vendor/supplier purchase orders' },
  { value: 'customers', label: 'Customer List', icon: Users, description: 'Customer names, contacts, addresses' },
  { value: 'vendors', label: 'Vendor List', icon: Users, description: 'Vendor names, contacts, addresses, terms' },
];

const Import = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [importType, setImportType] = useState('auto');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [statusMsg, setStatusMsg] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && (selectedFile.type === 'text/csv' || selectedFile.name.endsWith('.csv'))) {
      setFile(selectedFile);
      setResult(null);
      setUploadProgress(0);
      setStatusMsg('');
    } else {
      alert('Please select a CSV file');
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setLoading(true);
    setResult(null);
    setUploadProgress(0);
    
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
    setStatusMsg(`Uploading file (${fileSizeMB} MB)...`);

    try {
      // Use XMLHttpRequest with FormData for streaming upload + progress
      const response = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const token = localStorage.getItem('access_token');
        
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 80) + 10;
            setUploadProgress(pct);
            setStatusMsg(`Uploading... ${Math.round(e.loaded / 1024)} KB / ${Math.round(e.total / 1024)} KB`);
          }
        };
        
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { resolve(JSON.parse(xhr.responseText)); }
            catch { reject(new Error('Invalid server response')); }
          } else if (xhr.status === 413) {
            reject(new Error('File too large. Try splitting into smaller files.'));
          } else {
            try {
              const err = JSON.parse(xhr.responseText);
              reject(new Error(err.detail || `Upload failed (${xhr.status})`));
            } catch {
              reject(new Error(`Upload failed with status ${xhr.status}`));
            }
          }
        };
        
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.ontimeout = () => reject(new Error('Upload timed out'));
        xhr.timeout = 600000; // 10 min timeout for large files
        
        const formData = new FormData();
        formData.append('file', file);
        formData.append('import_type', importType);
        
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        xhr.open('POST', `${API_URL}/api/import/upload-csv`);
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        // Don't set Content-Type - browser auto-sets it with multipart boundary
        xhr.send(formData);
      });

      setUploadProgress(90);
      setStatusMsg('Processing on server...');

      // Poll for background job status
      if (response.job_id) {
        setResult({ ...response, polling: true });
        setStatusMsg(`Processing ${response.total_rows?.toLocaleString() || ''} rows (${response.import_type})...`);
        let jobDone = false;
        let pollCount = 0;
        let pollErrors = 0;
        while (!jobDone && pollCount < 600) {
          await new Promise(r => setTimeout(r, 1500));
          pollCount++;
          try {
            const jobStatus = await api.request(`/api/import/status/${response.job_id}`);
            pollErrors = 0;
            if (jobStatus.status === 'completed') {
              setResult(jobStatus.result);
              setUploadProgress(100);
              setStatusMsg('Complete!');
              jobDone = true;
            } else if (jobStatus.status === 'failed') {
              setResult(null);
              setStatusMsg('');
              alert('Import failed: ' + (jobStatus.error || 'Unknown error'));
              jobDone = true;
            } else {
              const prog = jobStatus.progress || 0;
              setUploadProgress(90 + Math.round(prog * 0.1));
              setStatusMsg(`Server processing... ${prog}% (${Math.floor(pollCount * 1.5)}s elapsed)`);
            }
          } catch (_pollErr) {
            pollErrors++;
            if (pollErrors >= 3) {
              alert('Lost connection to import job. The import may still be processing in the background. Please refresh and check your data.');
              jobDone = true;
            }
          }
        }
        if (!jobDone) {
          alert('Import is taking longer than expected. It may still be processing in the background. Please refresh and check your data.');
        }
      } else {
        setResult(response);
        setUploadProgress(100);
        setStatusMsg('Complete!');
      }
      setFile(null);
    } catch (error) {
      alert('Import failed: ' + error.message);
      setStatusMsg('');
      setUploadProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const getImportTypeLabel = (type) => {
    switch (type) {
      case 'sales_orders': return 'Sales Orders';
      case 'purchase_orders': return 'Purchase Orders';
      case 'product_details': return 'Product Details';
      case 'customers': return 'Customer List';
      case 'vendors': return 'Vendor List';
      case 'stock_levels': return 'Stock Levels';
      case 'reorder_settings': return 'Reorder Settings';
      default: return 'Data';
    }
  };

  const getImportTypeIcon = (type) => {
    switch (type) {
      case 'sales_orders': return <ShoppingCart className="h-5 w-5 text-green-600" />;
      case 'purchase_orders': return <Truck className="h-5 w-5 text-green-600" />;
      case 'customers': return <Users className="h-5 w-5 text-green-600" />;
      case 'vendors': return <Users className="h-5 w-5 text-green-600" />;
      case 'stock_levels': return <BarChart3 className="h-5 w-5 text-green-600" />;
      default: return <Package className="h-5 w-5 text-green-600" />;
    }
  };

  return (
    <div className="space-y-6" data-testid="import-page">
      <div>
        <h2 className="text-3xl font-semibold tracking-tight">Import from Inflow</h2>
        <p className="text-muted-foreground">Import products, sales orders, or purchase orders from Inflow CSV exports</p>
      </div>

      <div className="bg-card p-6 rounded-lg border border-border max-w-2xl">
        <h3 className="text-lg font-semibold mb-4">Upload CSV File</h3>
        
        <div className="space-y-4">
          {/* Import Type Selector */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Import Type
            </label>
            <select
              value={importType}
              onChange={(e) => setImportType(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
              data-testid="import-type-select"
            >
              {IMPORT_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label} — {type.description}
                </option>
              ))}
            </select>
          </div>

          {/* File Upload */}
          <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
            <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            
            {file ? (
              <div className="space-y-2">
                <div className="flex items-center justify-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  <p className="font-medium" data-testid="import-filename">{file.name}</p>
                </div>
                <p className="text-sm text-muted-foreground">{(file.size / 1024).toFixed(2)} KB</p>
                <button
                  onClick={() => setFile(null)}
                  className="text-sm text-red-600 hover:text-red-700"
                  data-testid="import-remove-btn"
                >
                  Remove
                </button>
              </div>
            ) : (
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="hidden"
                  data-testid="import-file-input"
                />
                <p className="font-medium text-primary hover:text-primary/80">Choose CSV file</p>
                <p className="text-sm text-muted-foreground mt-1">or drag and drop</p>
              </label>
            )}
          </div>

          {/* Import Button */}
          {file && (
            <button
              onClick={handleImport}
              disabled={loading}
              className="w-full py-3 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
              data-testid="import-submit-btn"
            >
              {loading ? (statusMsg || 'Processing...') : `Import ${IMPORT_TYPES.find(t => t.value === importType)?.label || 'Data'}`}
            </button>
          )}
          
          {/* Upload Progress Bar */}
          {loading && uploadProgress > 0 && (
            <div className="mt-3" data-testid="progress-bar">
              <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                <div className="bg-primary h-3 rounded-full transition-all duration-300" style={{width: `${uploadProgress}%`}}></div>
              </div>
              <p className="text-sm text-muted-foreground mt-1 text-center">{uploadProgress}% — {statusMsg}</p>
            </div>
          )}
        </div>

        {/* Results */}
        {result && (
          <div className="mt-6 p-4 bg-muted rounded-md" data-testid="import-result">
            <div className="flex items-start gap-3 mb-3">
              {result.success ? (
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {result.import_type && getImportTypeIcon(result.import_type)}
                  <p className="font-semibold text-green-700">
                    {getImportTypeLabel(result.import_type)} Import Complete
                  </p>
                </div>
                <div className="mt-2 space-y-1 text-sm">
                  {/* Product Details Results */}
                  {result.import_type === 'product_details' && (
                    <>
                      <p>Products created: <strong>{result.products_created}</strong></p>
                      {result.products_updated > 0 && (
                        <p>Products updated: <strong>{result.products_updated}</strong></p>
                      )}
                      <p>Variants created: <strong>{result.variants_created}</strong></p>
                    </>
                  )}
                  
                  {/* Sales Orders Results */}
                  {result.import_type === 'sales_orders' && (
                    <>
                      <p>Orders created: <strong>{result.orders_created}</strong></p>
                      {result.orders_updated > 0 && (
                        <p>Orders updated: <strong>{result.orders_updated}</strong></p>
                      )}
                      <p>Line items imported: <strong>{result.line_items_total}</strong></p>
                      {result.products_synced > 0 && (
                        <p>New products synced: <strong>{result.products_synced}</strong></p>
                      )}
                    </>
                  )}
                  
                  {/* Purchase Orders Results */}
                  {result.import_type === 'purchase_orders' && (
                    <>
                      <p>Purchase orders created: <strong>{result.orders_created}</strong></p>
                      {result.orders_updated > 0 && (
                        <p>Purchase orders updated: <strong>{result.orders_updated}</strong></p>
                      )}
                      <p>Line items imported: <strong>{result.line_items_total}</strong></p>
                    </>
                  )}

                  {/* Customer List Results */}
                  {result.import_type === 'customers' && (
                    <>
                      <p>Customers created: <strong>{result.customers_created}</strong></p>
                      {result.customers_updated > 0 && (
                        <p>Customers updated: <strong>{result.customers_updated}</strong></p>
                      )}
                    </>
                  )}

                  {/* Vendor List Results */}
                  {result.import_type === 'vendors' && (
                    <>
                      <p>Vendors created: <strong>{result.vendors_created}</strong></p>
                      {result.vendors_updated > 0 && (
                        <p>Vendors updated: <strong>{result.vendors_updated}</strong></p>
                      )}
                    </>
                  )}

                  {/* Stock Levels Results */}
                  {result.import_type === 'stock_levels' && (
                    <>
                      <p>Inventory records updated: <strong>{result.records_updated}</strong></p>
                      {result.variants_created > 0 && (
                        <p>New variants created: <strong>{result.variants_created}</strong></p>
                      )}
                      {result.not_found > 0 && (
                        <p className="text-amber-600">Products not found: {result.not_found}</p>
                      )}
                    </>
                  )}

                  {/* Reorder Settings Results */}
                  {result.import_type === 'reorder_settings' && (
                    <>
                      <p>Reorder settings updated: <strong>{result.records_updated}</strong></p>
                      {result.not_found > 0 && (
                        <p className="text-amber-600">Products not found: {result.not_found}</p>
                      )}
                    </>
                  )}

                  {/* Fallback */}
                  {!result.import_type && (
                    <>
                      {result.products_created !== undefined && <p>Products created: <strong>{result.products_created}</strong></p>}
                      {result.variants_created !== undefined && <p>Variants created: <strong>{result.variants_created}</strong></p>}
                      {result.orders_created !== undefined && <p>Orders created: <strong>{result.orders_created}</strong></p>}
                    </>
                  )}
                  
                  {result.skipped > 0 && <p className="text-amber-600">Rows/items skipped: {result.skipped}</p>}
                  
                  {result.detected_columns && result.detected_columns.length > 0 && (
                    <details className="mt-2">
                      <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                        Show detected columns ({result.detected_columns.length})
                      </summary>
                      <p className="text-xs text-muted-foreground mt-1">
                        {result.detected_columns.join(', ')}
                      </p>
                    </details>
                  )}
                  
                  {result.errors && result.errors.length > 0 && (
                    <p className="text-red-600 mt-1">{result.errors.length} errors occurred</p>
                  )}
                </div>
              </div>
            </div>
            
            {result.errors && result.errors.length > 0 && (
              <div className="mt-3 pt-3 border-t border-border">
                <p className="text-sm font-medium mb-2">Errors:</p>
                <div className="space-y-1">
                  {result.errors.slice(0, 10).map((error, idx) => (
                    <p key={idx} className="text-xs text-red-600">{error}</p>
                  ))}
                  {result.errors.length > 10 && (
                    <p className="text-xs text-muted-foreground">...and {result.errors.length - 10} more</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-2xl">
        <h4 className="font-semibold text-blue-900 mb-3">Supported inFlow CSV Exports</h4>
        <div className="text-sm text-blue-800 space-y-4">
          <div>
            <p className="font-medium flex items-center gap-1">
              <Package className="h-4 w-4" /> Product Details
            </p>
            <p className="text-xs mt-1">Columns: <code className="bg-blue-100 px-1 rounded">ProductName</code>, <code className="bg-blue-100 px-1 rounded">SKU</code>, <code className="bg-blue-100 px-1 rounded">Price</code>, <code className="bg-blue-100 px-1 rounded">Cost</code>, <code className="bg-blue-100 px-1 rounded">Category</code>, <code className="bg-blue-100 px-1 rounded">Barcode</code>, <code className="bg-blue-100 px-1 rounded">Quantity</code></p>
          </div>
          <div>
            <p className="font-medium flex items-center gap-1">
              <ShoppingCart className="h-4 w-4" /> Sales Orders
            </p>
            <p className="text-xs mt-1">Columns: <code className="bg-blue-100 px-1 rounded">OrderNumber</code>, <code className="bg-blue-100 px-1 rounded">Customer</code>, <code className="bg-blue-100 px-1 rounded">ProductName</code>, <code className="bg-blue-100 px-1 rounded">ProductSKU</code>, <code className="bg-blue-100 px-1 rounded">ProductQuantity</code>, <code className="bg-blue-100 px-1 rounded">ProductUnitPrice</code></p>
          </div>
          <div>
            <p className="font-medium flex items-center gap-1">
              <Truck className="h-4 w-4" /> Purchase Orders
            </p>
            <p className="text-xs mt-1">Columns: <code className="bg-blue-100 px-1 rounded">OrderNumber</code>, <code className="bg-blue-100 px-1 rounded">Vendor</code>, <code className="bg-blue-100 px-1 rounded">ProductName</code>, <code className="bg-blue-100 px-1 rounded">ProductSKU</code>, <code className="bg-blue-100 px-1 rounded">ProductQuantity</code>, <code className="bg-blue-100 px-1 rounded">ProductUnitPrice</code></p>
          </div>
          <div>
            <p className="font-medium flex items-center gap-1">
              <Users className="h-4 w-4" /> Customer List
            </p>
            <p className="text-xs mt-1">Columns: <code className="bg-blue-100 px-1 rounded">Name</code> or <code className="bg-blue-100 px-1 rounded">Customer</code>, <code className="bg-blue-100 px-1 rounded">Email</code>, <code className="bg-blue-100 px-1 rounded">Phone</code>, <code className="bg-blue-100 px-1 rounded">Address</code>, <code className="bg-blue-100 px-1 rounded">City</code>, <code className="bg-blue-100 px-1 rounded">State</code></p>
          </div>
          <div>
            <p className="font-medium flex items-center gap-1">
              <Users className="h-4 w-4" /> Vendor List
            </p>
            <p className="text-xs mt-1">Columns: <code className="bg-blue-100 px-1 rounded">Vendor</code> or <code className="bg-blue-100 px-1 rounded">Name</code>, <code className="bg-blue-100 px-1 rounded">Email</code>, <code className="bg-blue-100 px-1 rounded">Phone</code>, <code className="bg-blue-100 px-1 rounded">Address</code>, <code className="bg-blue-100 px-1 rounded">City</code>, <code className="bg-blue-100 px-1 rounded">State</code>, <code className="bg-blue-100 px-1 rounded">PaymentTerms</code>, <code className="bg-blue-100 px-1 rounded">AccountNumber</code></p>
          </div>
          <div>
            <p className="font-medium flex items-center gap-1">
              <BarChart3 className="h-4 w-4" /> Stock Levels
            </p>
            <p className="text-xs mt-1">Columns: <code className="bg-blue-100 px-1 rounded">ProductName</code>, <code className="bg-blue-100 px-1 rounded">SKU</code>, <code className="bg-blue-100 px-1 rounded">QuantityOnHand</code>, <code className="bg-blue-100 px-1 rounded">Location</code></p>
          </div>
          <div>
            <p className="font-medium flex items-center gap-1">
              <AlertTriangle className="h-4 w-4" /> Reorder Settings
            </p>
            <p className="text-xs mt-1">Columns: <code className="bg-blue-100 px-1 rounded">ProductName</code> or <code className="bg-blue-100 px-1 rounded">SKU</code>, <code className="bg-blue-100 px-1 rounded">Reorder Point</code>, <code className="bg-blue-100 px-1 rounded">Reorder Quantity</code></p>
          </div>
          <p className="text-xs mt-2 border-t border-blue-200 pt-2">
            Tip: If auto-detect picks the wrong type, select the correct type from the dropdown above before importing.
          </p>
        </div>
      </div>
      {/* Clear & Re-import Section */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 max-w-2xl">
        <h4 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" /> Clear Imported Data for Re-import
        </h4>
        <p className="text-sm text-amber-800 mb-4">
          If you need to re-import with full details (addresses, contacts, shipping, tax), clear the old data first. This only removes data originally imported from inFlow.
        </p>
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'products', label: 'Products & Inventory' },
            { key: 'sales_orders', label: 'Sales Orders' },
            { key: 'purchase_orders', label: 'Purchase Orders' },
            { key: 'customers', label: 'Customers' },
            { key: 'vendors', label: 'Vendors' },
          ].map(({ key, label }) => (
            <button key={key}
              onClick={async () => {
                if (!window.confirm(`Are you sure you want to delete ALL imported ${label}? This cannot be undone.`)) return;
                try {
                  const res = await api.clearImportedData(key);
                  alert(`Cleared ${res.deleted_count} ${label.toLowerCase()}. You can now re-import.`);
                } catch (e) {
                  alert('Failed: ' + e.message);
                }
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-amber-300 text-amber-800 rounded-md hover:bg-amber-100 transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" /> Clear {label}
            </button>
          ))}
        </div>
      </div>

      {/* Separate Vendors Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-2xl" data-testid="separate-vendors-section">
        <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
          <ArrowRightLeft className="h-4 w-4" /> Separate Vendors from Customers
        </h4>
        <p className="text-sm text-blue-800 mb-4">
          If vendors ended up in the Customers list (common with inFlow imports), click below to automatically detect and move them to the Vendors collection based on inFlow column patterns.
        </p>
        <button
          data-testid="separate-vendors-btn"
          onClick={async () => {
            if (!window.confirm('This will scan the Customers collection and move detected vendor records to the Vendors collection. Continue?')) return;
            try {
              const res = await api.request('/api/import/separate-vendors', { method: 'POST' });
              alert(res.message || `Moved ${res.moved_to_vendors} vendors. ${res.kept_as_customers} remain as customers.`);
            } catch (e) {
              alert('Failed: ' + e.message);
            }
          }}
          className="flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
        >
          <ArrowRightLeft className="h-4 w-4" /> Auto-Separate Vendors
        </button>
      </div>
    </div>
  );
};

export default Import;
